<template>
	<PageLoading
		:loading="!user.uuid || cases.rewardsLoading"
		class="relative mt-4"
	>
		<PageTitle>{{ t('title') }}</PageTitle>
		<PagePadding class="mt-5">
			<BaseCard class="p-5">
				<div
					class="text-label-primary flex items-center gap-2 text-sm font-medium uppercase"
				>
					<LazyImage
						:src="avatarSm(user.avatar)"
						:alt="user.name"
						class="h-6 w-6 shrink-0 rounded-full"
					/>
					<div class="truncate">
						{{ user.name }}
					</div>
					<LevelPill class="ml-auto">{{ t('level', [user.level]) }}</LevelPill>
				</div>
				<ProgressBar
					color="yellow"
					class="mt-4"
					:value="user.levelPercent / 100"
				/>
				<div
					class="text-label-tertiary mt-3 flex items-center justify-between gap-2 text-sm font-medium capitalize"
				>
					<div>{{ t('progress') }}</div>
					<div>{{ xp }}/{{ xpLevel }} XP</div>
				</div>
			</BaseCard>
			<SubtitleText class="mt-5" v-if="freeCases">
				{{ t('freeCases') }}
			</SubtitleText>
			<div class="mt-2 grid grid-cols-2 gap-1.5" v-if="freeCases">
				<RouterLink
					:to="`/onboarding`"
					custom
					v-slot="{ navigate }"
					v-if="cases.welcomeCase && cases.welcomeCase.count"
				>
					<CaseCard
						:uuid="cases.welcomeCase.uuid"
						:name="cases.welcomeCase.name"
						@click="navigate"
						class="relative *:pt-0"
						centered
					>
						<AccentLabel
							:value="t('left', [cases.welcomeCase.count])"
							:active="true"
							class="absolute top-3 right-3"
						/>
						<div class="-mt-1 px-3 pb-3">
							<BaseButton size="md" color="primary" class="w-full uppercase">
								{{ t('open') }}
							</BaseButton>
						</div>
					</CaseCard>
				</RouterLink>
				<RouterLink
					:to="
						cases.referralCase.canOpen
							? `/case/${cases.referralCase.uuid}`
							: `/invites`
					"
					custom
					v-slot="{ navigate }"
					v-if="
						cases.referralCase &&
						!(
							!cases.referralCase.canOpen &&
							cases.referralCase.requiredReferrals <=
								cases.referralCase.qualifiedReferrals
						)
					"
				>
					<CaseCard
						:uuid="cases.referralCase.uuid"
						:name="'Gold Mine'"
						@click="navigate"
						class="relative *:pt-0"
						centered
					>
						<div class="-mt-1 px-3 pb-3">
							<BaseButton
								size="md"
								:color="cases.referralCase.canOpen ? 'primary' : 'secondary'"
								class="w-full uppercase"
							>
								{{ cases.referralCase.canOpen ? t('open') : t('preview') }}
							</BaseButton>
						</div>
					</CaseCard>
				</RouterLink>
			</div>
			<div
				class="mt-5 flex items-center justify-between"
				v-if="unlockedCases.length"
			>
				<SubtitleText>{{ t('available') }}</SubtitleText>
				<div
					class="flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium"
					:class="{
						'text-label-primary bg-[#3F2025]': expiring,
						'text-label-tertiary bg-fill-secondary': !expiring
					}"
					v-if="cases.lockedUntil !== '00:00:00'"
				>
					<FireIcon class="h-5 w-5 text-[#E01533]" v-if="expiring" />
					<HourglassIcon class="h-5 w-5" v-else />
					<div class="w-13.5 shrink-0 text-center">
						{{ cases.lockedUntil }}
					</div>
				</div>
			</div>
			<WrappedCard class="mt-2" topClass="p-5">
				<template #top>
					<div class="h-11 w-11 rounded-full bg-[#3F3F3F] p-2">
						<DailyIcon class="text-label-secondary h-7 w-7" />
					</div>
					<div>
						<div class="text-label-primary text-sm font-medium capitalize">
							{{ t('note.title') }}
						</div>
						<div class="text-label-secondary mt-1 text-sm">
							{{ unlockedCases.length ? t('note.desc1') : t('note.desc2') }}
						</div>
					</div>
				</template>
				<template #bottom>
					<NoteIcon class="text-label-secondary h-5 w-5 shrink-0" />
					<div class="text-label-secondary text-sm">
						{{ t('note.tip') }}
					</div>
				</template>
			</WrappedCard>
			<div class="mt-2 grid grid-cols-2 gap-1.5">
				<RouterLink
					:to="`/case/${item.uuid}`"
					custom
					v-slot="{ navigate }"
					v-for="item in unlockedCases"
				>
					<CaseCard
						:uuid="item.uuid"
						:name="item.name"
						:modelUuid="item.wonItem?.modelUuid || undefined"
						@click="navigate"
						class="relative *:pt-0"
						centered
					>
						<div class="-mt-1 px-3 pb-3">
							<BaseButton
								size="md"
								:color="item.wonItem ? 'secondary' : 'primary'"
								class="w-full uppercase"
							>
								{{ item.wonItem ? t('view') : t('open') }}
							</BaseButton>
						</div>
					</CaseCard>
				</RouterLink>
			</div>
			<div
				class="mt-5 flex items-center justify-between"
				v-if="lockedCases.length"
			>
				<SubtitleText>
					{{ t('locked') }}
				</SubtitleText>
				<div
					class="flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium"
					:class="{
						'text-label-primary bg-[#3F2025]': expiring,
						'text-label-tertiary bg-fill-secondary': !expiring
					}"
					v-if="!unlockedCases.length && cases.lockedUntil !== '00:00:00'"
				>
					<FireIcon class="h-5 w-5 text-[#E01533]" v-if="expiring" />
					<HourglassIcon class="h-5 w-5" v-else />
					<div class="w-13.5 shrink-0 text-center">
						{{ cases.lockedUntil }}
					</div>
				</div>
			</div>
			<div class="mt-2 grid grid-cols-2 gap-1.5">
				<RouterLink
					:to="`/case/${item.uuid}`"
					custom
					v-slot="{ navigate }"
					v-for="item in lockedCases"
				>
					<CaseCard
						:uuid="item.uuid"
						:name="item.name"
						:modelUuid="item.wonItem?.modelUuid || undefined"
						locked
						:lockedText="item.lockedText"
						@click="navigate"
						centered
						class="*:pt-0"
					>
						<div class="-mt-1 px-3 pb-3">
							<BaseButton size="md" color="secondary" class="w-full uppercase">
								{{ t('preview') }}
							</BaseButton>
						</div>
					</CaseCard>
				</RouterLink>
			</div>
		</PagePadding>
	</PageLoading>
</template>
<script setup lang="ts">
import PageTitle from '@/components/PageTitle.vue'
import PagePadding from '@/components/PagePadding.vue'
import SubtitleText from '@/components/SubtitleText.vue'
import LazyImage from '@/components/LazyImage.vue'
import { computed, watch } from 'vue'
import ProgressBar from '@/components/ProgressBar.vue'
import BaseCard from '@/components/BaseCard.vue'
import InfoAlert from '@/components/InfoAlert.vue'
import CaseCard from '@/components/CaseCard.vue'
import BaseButton from '@/components/BaseButton.vue'
import { useLayout } from '@/stores/layout'
import { useUser } from '@/stores/user'
import PageLoading from '@/components/PageLoading.vue'
import { avatarSm } from '@/utils/urls'
import { useCases } from '@/stores/cases'
import { useI18n } from 'vue-i18n'
import FireIcon from '@/assets/icons/fire.svg'
import HourglassIcon from '@/assets/icons/hourglass.svg'
import LevelPill from '@/components/LevelPill.vue'
import AccentLabel from '@/components/AccentLabel.vue'
import WrappedCard from '@/components/WrappedCard.vue'
import DailyIcon from '@/assets/icons/daily.svg'
import NoteIcon from '@/assets/icons/note.svg'

const { t } = useI18n({
	messages: {
		en: {
			title: 'rewards',
			yourLevel: 'Your Level',
			level: 'level {0}',
			progress: 'Progress',
			available: 'Daily Cases',
			locked: 'Locked daily cases',
			expire: 'Expire in',
			note: {
				title: 'get free cases daily',
				desc1:
					'Prizes are subject to change depending on your unboxing activity.',
				desc2: 'Reach level 10 to unlock them.',
				tip: 'Collect rewards daily or they get worse.'
			},
			preview: 'preview',
			view: 'view',
			open: 'open',
			freeCases: 'Free Cases',
			left: 'x{0} left'
		},
		ru: {
			title: 'награды',
			yourLevel: 'Твой уровень',
			level: 'уровень {0}',
			progress: 'Прогресс',
			available: 'Ежедневные кейсы',
			locked: 'Заблокированные кейсы',
			expire: 'Истекает',
			note: {
				title: 'Открывай Ежедневные бесплатные кейсы',
				desc1: 'Дроп может меняться в зависимости от твоей активности.',
				desc2: 'Подними 10 уровень, чтобы разблокировать их.',
				tip: 'Собирай награды ежедневно, иначе они станут хуже.'
			},
			preview: 'посмотреть',
			view: 'посмотреть',
			open: 'открыть',
			freeCases: 'Бесплатные кейсы',
			left: 'еще {0} шт.'
		}
	}
})

const layout = useLayout()

layout.showBottomNavbar()

const user = useUser()
const cases = useCases()

watch(
	() => user.uuid,
	() => {
		if (!user.uuid) {
			return
		}

		if (!cases.rewards.length) {
			cases.getRewardCases()
		}
	},
	{ immediate: true }
)

const formatNumberWithSpaces = (num: number): string => {
	return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

const xp = computed(() => {
	return formatNumberWithSpaces(user.xp)
})

const xpLevel = computed(() => {
	return formatNumberWithSpaces(user.xpLevel)
})

const levelLessThan = (name: string, level: number) => {
	// Level N
	const caseLevel = parseInt(name.split(' ')[1] || '0')

	if (!caseLevel) {
		return
	}

	return caseLevel <= level
}

const unlockedCases = computed(() => cases.rewards.filter((c) => !c.isLocked))
const lockedCases = computed(() =>
	cases.rewards
		.filter((c) => c.isLocked)
		.map((item) => ({
			...item,
			lockedText: levelLessThan(item.name, user.level)
				? cases.lockedUntil
				: undefined
		}))
)

const expiring = computed(() => !unlockedCases.value.every((c) => c.wonItem))

const freeCases = computed(() => {
	if (cases.welcomeCase && cases.welcomeCase.count) {
		return true
	}
	if (
		cases.referralCase &&
		cases.referralCase.requiredReferrals > cases.referralCase.qualifiedReferrals
	) {
		return true
	}
	if (cases.referralCase?.canOpen) {
		return true
	}

	return false
})
</script>
